import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  AlertCircle, CheckCircle, XCircle, Clock, 
  AlertTriangle, Shield, Eye, ThumbsUp, ThumbsDown,
  RefreshCw, Filter
} from 'lucide-react';

const API_URL = getApiBaseUrl();

const AIReviewQueue = () => {
  const [queueItems, setQueueItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState({
    status: 'pending',
    domain: '',
    riskClass: '',
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = new URLSearchParams({
        status: filter.status,
        page: page.toString(),
        page_size: '20',
      });
      if (filter.domain) params.append('domain', filter.domain);
      if (filter.riskClass) params.append('risk_class', filter.riskClass);

      const response = await fetch(`${API_URL}/api/ai/review/queue?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setQueueItems(data.items || []);
      setHasMore(data.has_more);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/review/queue/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchItemDetails = async (itemId) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/review/queue/${itemId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedItem(data);
    } catch (error) {
      console.error('Failed to fetch item details:', error);
    }
  };

  const resolveItem = async (itemId, action) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/ai/review/queue/${itemId}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resolution_action: action,
          resolution_notes: `Resolved via admin UI`,
        }),
      });
      fetchQueue();
      fetchStats();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to resolve item:', error);
    }
  };

  useEffect(() => {
    fetchQueue();
    fetchStats();
  }, [fetchQueue]);

  const getRiskIcon = (riskClass) => {
    switch (riskClass) {
      case 'critical': return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'high': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'medium': return <Shield className="h-5 w-5 text-yellow-500" />;
      default: return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Review Queue</h1>
            <p className="text-gray-600">Review and approve AI-generated decisions</p>
          </div>
          <Button onClick={() => { fetchQueue(); fetchStats(); }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold">{stats.total_pending}</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Overdue</p>
                    <p className="text-2xl font-bold text-red-600">{stats.overdue_count}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Avg Wait</p>
                    <p className="text-2xl font-bold">
                      {stats.avg_wait_hours ? `${stats.avg_wait_hours.toFixed(1)}h` : '-'}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div>
                  <p className="text-sm text-gray-500 mb-2">By Risk</p>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(stats.pending_by_risk_class || {}).map(([risk, count]) => (
                      <span key={risk} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {risk}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
              <select
                value={filter.domain}
                onChange={(e) => setFilter({ ...filter, domain: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Domains</option>
                <option value="screening">Screening</option>
                <option value="arrears">Arrears</option>
                <option value="maintenance">Maintenance</option>
                <option value="comms">Communications</option>
              </select>
              <select
                value={filter.riskClass}
                onChange={(e) => setFilter({ ...filter, riskClass: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Risk Levels</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Queue Items */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Item List */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <Card><CardContent className="p-8 text-center">Loading...</CardContent></Card>
            ) : queueItems.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No items in queue</p>
                </CardContent>
              </Card>
            ) : (
              queueItems.map((item) => (
                <Card 
                  key={item.id}
                  className={`cursor-pointer transition-all ${
                    selectedItem?.id === item.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                  }`}
                  onClick={() => fetchItemDetails(item.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getRiskIcon(item.risk_class)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{item.domain}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-blue-600">{item.proposed_action}</span>
                            {getStatusBadge(item.status)}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{item.review_reason}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span>Confidence: {(item.confidence * 100).toFixed(0)}%</span>
                            <span>Priority: {item.priority}</span>
                            <span>{new Date(item.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      {item.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); resolveItem(item.id, 'approved'); }}
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); resolveItem(item.id, 'rejected'); }}
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {/* Pagination */}
            {(hasMore || page > 1) && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="px-4 py-2">Page {page}</span>
                <Button
                  variant="outline"
                  disabled={!hasMore}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedItem ? (
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Decision Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Domain & Action</h4>
                    <p className="text-lg font-semibold capitalize">
                      {selectedItem.domain} → {selectedItem.proposed_action}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Confidence</h4>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            selectedItem.confidence >= 0.75 ? 'bg-green-500' :
                            selectedItem.confidence >= 0.50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${selectedItem.confidence * 100}%` }}
                        />
                      </div>
                      <span className="font-medium">{(selectedItem.confidence * 100).toFixed(0)}%</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Review Reason</h4>
                    <p className="text-sm">{selectedItem.review_reason}</p>
                  </div>

                  {selectedItem.explainability && (
                    <>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Features Used</h4>
                        <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
                          {JSON.stringify(selectedItem.explainability.features, null, 2)}
                        </pre>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Policies Checked</h4>
                        <ul className="text-xs space-y-1">
                          {(selectedItem.explainability.policies_checked || []).map((policy, i) => (
                            <li key={i} className="bg-gray-50 px-2 py-1 rounded">{policy}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {selectedItem.status === 'pending' && (
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => resolveItem(selectedItem.id, 'approved')}
                      >
                        <ThumbsUp className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => resolveItem(selectedItem.id, 'rejected')}
                      >
                        <ThumbsDown className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select an item to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReviewQueue;
